// src/components/theme/elements/ThemeNavInstruction.js

import React, { useRef, useState, useEffect } from 'react';
import { Group, Text, Rect } from 'react-konva';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';

const scaleFactor = 1.9;

/**
 * ThemeNavInstructionSettings Component
 * Provides UI controls to configure the ThemeNavInstruction element.
 * Allows users to set the position (x, y), text color, and font size.
 */
export const ThemeNavInstructionSettings = ({ element, updateElement }) => {

  /**
   * Handler for X position change.
   * Updates the X position of the element.
   */
  const handleXChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x)) {
      updateElement(element.id, 'x', x);
    }
  };

  /**
   * Handler for Y position change.
   * Updates the Y position of the element.
   */
  const handleYChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y)) {
      updateElement(element.id, 'y', y);
    }
  };

  /**
   * Handler for font size change.
   * Updates the font size property.
   * @param {object} e - The event object.
   */
  const handleSizeChange = (e) => {
    const size = parseInt(e.target.value, 10);
    if (!isNaN(size) && [10, 14, 18].includes(size)) {
      updateElement(element.id, 'size', size);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className={styles.container}>
      {/* Position Controls */}
      <label className={styles.label}>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          aria-label="Set X Position"
        />
      </label>

      <label className={styles.label}>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          aria-label="Set Y Position"
        />
      </label>

      <CustomColorPicker
        label="Color"
        color={element.color}
        onChange={(value) => updateElement(element.id, 'color', value)}
      />

      {/* Font Size */}
      <label className={styles.label}>
        Font Size:
        <select
          className={styles.select100}
          value={element.size}
          onChange={handleSizeChange}
          aria-label="Set Font Size"
        >
          <option value={10}>10</option>
          <option value={14}>14</option>
          <option value={18}>18</option>
        </select>
      </label>
    </div>
  );
};

ThemeNavInstructionSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    textColor: PropTypes.string, // Text Color (optional) - Default to black
    size: PropTypes.number.isRequired, // Font Size (10, 14, 18)
    isEnabled: PropTypes.bool, // Enabled state (optional)
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};

/**
 * ThemeNavInstruction Component
 * Renders the instruction text on the canvas using Konva shapes.
 * Utilizes only two properties: Text Color and Font Size.
 * The text is fixed as "Turn right on Mandalorian Blvd".
 */
const ThemeNavInstruction = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  render,editAllowed,
  scale,
  snapToGridEnabled,
  gridSize
}) => {
  const textRef = useRef(null);
  const [textDimensions, setTextDimensions] = useState({ width: 0, height: 0 });
  const [fontLoaded, setFontLoaded] = useState(false);

  const displayText = "Turn right on Mandalorian Blvd";

  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    updateElement(element.id, 'x', Math.round(x));
    updateElement(element.id, 'y', Math.round(y));
  };

  useEffect(() => {
    if (document.fonts) {
      document.fonts.load(`10pt 'ForcedSquare'`).then(() => {
        setFontLoaded(true);
      });
    } else {
      setFontLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (fontLoaded && textRef.current) {
      const textWidth = textRef.current.width() - 2;
      const textHeight = textRef.current.height();
      setTextDimensions({ width: textWidth, height: textHeight });
    }
  }, [fontLoaded, displayText, element.size]);

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  // Calculate offsets based on font size and text dimensions for proper alignment
  const offsetX = 0;
  const offsetY = textDimensions.height / 2;

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed} // Make draggable only when render is false
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, 120, element.size * scaleFactor) }}
      onClick={() => setSelectedElement(element)}
      onTap={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
      aria-label="NavInstruction Indicator"
    >
      {/* Instruction Text */}
      {fontLoaded && (
        <Text
          ref={textRef}
          text={displayText}
          fontSize={element.size * scaleFactor}
          fill={element.color}
          fontFamily="'ForcedSquare', monospace"
          draggable={false}
          align='center'
          offsetX={offsetX}
          offsetY={element.size * 0.2}
        />
      )}

      {!render && fontLoaded && textDimensions.width > 0 && textDimensions.height > 0 && (
        <Rect
          x={0}
          y={0}
          width={400}
          height={textDimensions.height * 0.82}
          stroke={"#1976d2"}
          dash={isSelected ? [1, 1] : [0, 0]}
          strokeWidth={0.5}
          listening={false}
        />
      )}
    </Group>
  );
};

ThemeNavInstruction.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    textColor: PropTypes.string, // Text Color (optional)
    size: PropTypes.number.isRequired, // Font Size (10, 14, 18)
    isEnabled: PropTypes.bool, // Enabled state (optional)
  }).isRequired,
  selectedElement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // ... other properties as needed
  }),
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeNavInstruction;
